/**
 * Small, dependency-free measurements for the processed subliminal lanes.
 *
 * These values describe samples observed in the browser graph. They are an
 * engineering/QA contract for level and routing, not a proxy for perception,
 * EEG, or a listener outcome.
 */

import { SUBLIMINAL_GAIN_CEILING } from "./subliminal-level.js";

const BYTE_CENTER = 128;
const BYTE_SCALE = 128;

function isByteArray(samples) {
  return samples instanceof Uint8Array || samples instanceof Uint8ClampedArray;
}

function toLinearSample(value, byteFormat) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return byteFormat ? (number - BYTE_CENTER) / BYTE_SCALE : number;
}

/** Measure RMS and absolute peak from a time-domain analyser buffer. */
export function measureTimeDomainSignal(samples, { format = "auto" } = {}) {
  const length = Number(samples?.length) || 0;
  const byteFormat = format === "byte" || (format === "auto" && isByteArray(samples));
  if (!length) return { rms: 0, peak: 0, sampleCount: 0 };

  let sumSquares = 0;
  let peak = 0;
  for (let index = 0; index < length; index += 1) {
    const sample = toLinearSample(samples[index], byteFormat);
    sumSquares += sample * sample;
    peak = Math.max(peak, Math.abs(sample));
  }
  return {
    rms: Math.sqrt(sumSquares / length),
    peak,
    sampleCount: length,
  };
}

export function measureTimeDomainBytes(samples) {
  return measureTimeDomainSignal(samples, { format: "byte" });
}

export function linearToDbfs(value) {
  const linear = Math.abs(Number(value));
  return linear > 0 && Number.isFinite(linear)
    ? 20 * Math.log10(linear)
    : Number.NEGATIVE_INFINITY;
}

/**
 * Add level-ceiling and activity decisions to a measured lane sample.
 * `ceilingToleranceLinear` is useful for 8-bit analyser quantization; it does
 * not change the underlying measured peak.
 */
export function summarizeSubliminalSignal({
  rms = 0,
  peak = 0,
  ceilingLinear = SUBLIMINAL_GAIN_CEILING,
  ceilingToleranceLinear = 0,
  activeFloorLinear = 0.0005,
} = {}) {
  const measuredRms = Math.max(0, Number(rms) || 0);
  const measuredPeak = Math.max(0, Number(peak) || 0);
  const ceiling = Math.max(0, Number(ceilingLinear) || 0);
  const tolerance = Math.max(0, Number(ceilingToleranceLinear) || 0);
  const activeFloor = Math.max(0, Number(activeFloorLinear) || 0);
  const rmsDbfs = linearToDbfs(measuredRms);
  const peakDbfs = linearToDbfs(measuredPeak);
  const ceilingDbfs = linearToDbfs(ceiling);
  return {
    rms: measuredRms,
    peak: measuredPeak,
    rmsDbfs,
    peakDbfs,
    ceilingLinear: ceiling,
    ceilingDbfs,
    peakMarginDb: Number.isFinite(peakDbfs) && Number.isFinite(ceilingDbfs)
      ? peakDbfs - ceilingDbfs
      : Number.NEGATIVE_INFINITY,
    overCeiling: measuredPeak > ceiling + tolerance,
    signalActive: measuredRms > activeFloor,
  };
}
